import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { color, tooltipStyle } from "./palette";
import { GlassDefs } from "./GlassDefs";

function Panel({ title, subtitle, children, testid, actions }) {
  return (
    <section className="glass-panel p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <div>
          <h3 className="font-display font-bold text-lg tracking-tight">{title}</h3>
          {subtitle && (
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/**
 * Reusable colourful line chart card.
 * Props:
 *   title, subtitle, testid, actions
 *   data: [{name, value}]
 *   height: tailwind class (default "h-72")
 *   xAxisAngle: tilt for long category labels (default -20)
 *   emptyText
 */
export default function LineChartCard({
  title,
  subtitle,
  testid,
  actions,
  data,
  height = "h-60 sm:h-72",
  xAxisAngle = -20,
  emptyText = "No data yet",
}) {
  const hasData = Array.isArray(data) && data.length > 0;
  return (
    <Panel title={title} subtitle={subtitle} testid={testid} actions={actions}>
      <div className={height}>
        {!hasData ? (
          <div className="h-full flex items-center justify-center text-sm text-neutral-500 font-mono uppercase tracking-wider">
            {emptyText}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 16, right: 24, left: 0, bottom: xAxisAngle ? 60 : 5 }}
            >
              <GlassDefs />
              <CartesianGrid stroke="rgba(100,116,139,0.18)" strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={xAxisAngle}
                textAnchor={xAxisAngle ? "end" : "middle"}
                height={xAxisAngle ? 70 : 30}
                stroke="rgba(100,116,139,0.35)" axisLine={false} tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fontFamily: "Inter" }}
                stroke="rgba(100,116,139,0.35)" axisLine={false} tickLine={false}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Area
                type="monotone"
                dataKey="value"
                fill="url(#glassg-a-6)"
                stroke="none"
                tooltipType="none"
                legendType="none"
                isAnimationActive
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color(6)}
                strokeWidth={3}
                dot={{ r: 5, fill: color(6), strokeWidth: 2, stroke: "rgba(255,255,255,0.9)" }}
                activeDot={{ r: 7 }}
                style={{ filter: "drop-shadow(0 6px 10px rgba(0,122,255,0.3))" }}
                isAnimationActive
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </Panel>
  );
}
